import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "../../../utils/notifications";
import { updateRfqItem, type RFQItemUpdate } from "../../../services/rfqItems";
import { queryKeys } from "../../../api/queryKeys";
import type { ErrorLike } from "../../../utils/errors";

/**
 * What a cached ["rfqs", ...] entry holds, as far as the optimistic patch
 * reads it: a list of RFQs, or infinite-query pages of them.
 */
type CachedRfq = { items?: Array<{ rfq_item_id: number }> };
type CachedRfqs = CachedRfq[] | { pages?: Array<CachedRfq[] | { data?: CachedRfq[] }> };

interface ItemMutationCallbacks {
  /** After the write succeeded and the rfqs queries were invalidated. */
  onSaved: (variables: RFQItemUpdate) => void;
  /** After a failed write was rolled back, before the error toast. */
  onFailed: () => void;
}

/**
 * The item row's single write: update one rfq_items row through the backend
 * (PUT /rfqs/:rfqId/items/:itemId, validated and audited), optimistically
 * patching every cached ["rfqs"] query first and rolling back on failure.
 */
export function useItemMutations(rfqId: number, rfqItemId: number, { onSaved, onFailed }: ItemMutationCallbacks) {
  const queryClient = useQueryClient();

  const updateItemMutation = useMutation({
    mutationFn: async (data: RFQItemUpdate) => {
      const response = await updateRfqItem(rfqId, rfqItemId, data);
      return { data: response };
    },
    onMutate: async (variables: RFQItemUpdate) => {
      // Cancel any outgoing refetches to avoid optimistic update being overwritten
      await queryClient.cancelQueries({ queryKey: queryKeys.rfqs.all });

      // Snapshot the previous value for rollback
      const previousRfqs = queryClient.getQueriesData({ queryKey: queryKeys.rfqs.all });

      // Optimistically update the cache
      queryClient.setQueriesData<CachedRfqs>({ queryKey: queryKeys.rfqs.all }, (old) => {
        if (!old) return old;

        // Handle both paginated and non-paginated data structures
        const updateItems = (items: CachedRfq[]) => {
          if (!items) return items;
          return items.map((rfq) => ({
            ...rfq,
            items: rfq.items?.map((itm) =>
              itm.rfq_item_id === rfqItemId
                ? { ...itm, ...variables }
                : itm
            )
          }));
        };

        // Check if old is paginated (has pages property)
        if (!Array.isArray(old) && old.pages) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: updateItems((!Array.isArray(page) && page.data) || (page as CachedRfq[]))
            }))
          };
        }

        // Non-paginated data
        return updateItems(old as CachedRfq[]);
      });

      return { previousRfqs };
    },
    onSuccess: (_response, variables) => {
      // Invalidate queries to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });

      onSaved(variables);
    },
    onError: (error: ErrorLike, variables, context) => {
      console.error(`Error updating item ${rfqItemId}:`, error);

      // Rollback optimistic update
      if (context?.previousRfqs) {
        context.previousRfqs.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      onFailed();

      // Determine which field was being updated
      const updateField = Object.keys(variables).length > 0
        ? Object.keys(variables)[0].replace(/_/g, ' ')
        : 'item information';

      // Extract detailed error information
      const errorMsg = error.message || '';
      const statusCode = error.response?.status || '';
      const serverError = error.response?.data?.error || '';

      toast({
        title: "Error Updating Item",
        description: `Failed to update ${updateField}: ${errorMsg || serverError || 'Server communication error'}${statusCode ? ` (Status: ${statusCode})` : ''}. Please try again.`,
        severity: "error",
      });
    },
  });

  return updateItemMutation;
}
